import { useState, useEffect } from 'react';
import API from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Users, MapPin, TrendingUp, Loader2 } from 'lucide-react';

export default function TeamLeaderDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await API.get('/dashboard/team-leader-stats');
        setStats(data);
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    load();
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;
  if (!stats) return <p className="text-slate-500">No data available</p>;

  return (
    <div data-testid="team-leader-dashboard">
      <div className="mb-6">
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">Team Dashboard</h1>
        <p className="text-slate-500 text-sm mt-1">Overview of your assigned area</p>
      </div>

      {/* Top Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="stat-card border-slate-200">
          <CardContent className="pt-5 pb-4 px-5">
            <p className="text-xs font-bold uppercase tracking-widest text-slate-500">Precincts</p>
            <p className="text-3xl font-bold text-slate-900 mt-1">{stats.num_precincts}</p>
          </CardContent>
        </Card>
        <Card className="stat-card border-slate-200">
          <CardContent className="pt-5 pb-4 px-5">
            <p className="text-xs font-bold uppercase tracking-widest text-slate-500">Surveyors</p>
            <p className="text-3xl font-bold text-slate-900 mt-1">{stats.num_surveyors}</p>
          </CardContent>
        </Card>
        <Card className="stat-card border-slate-200">
          <CardContent className="pt-5 pb-4 px-5">
            <p className="text-xs font-bold uppercase tracking-widest text-slate-500">Total Voters</p>
            <p data-testid="tl-total-voters" className="text-3xl font-bold text-slate-900 mt-1">{stats.total_voters}</p>
          </CardContent>
        </Card>
        <Card className="stat-card border-slate-200">
          <CardContent className="pt-5 pb-4 px-5">
            <p className="text-xs font-bold uppercase tracking-widest text-slate-500">Completion</p>
            <p data-testid="tl-completion" className="text-3xl font-bold text-blue-600 mt-1">{stats.completion_pct}%</p>
          </CardContent>
        </Card>
      </div>

      {/* Precinct Progress */}
      <Card className="mb-6 border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <MapPin className="w-5 h-5 text-blue-500" /> Precinct Progress
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {(stats.assigned_precincts || []).map(p => (
              <div key={p.id}>
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-slate-900 text-sm font-mono">{p.precinct_number}</span>
                  <span className="text-xs text-slate-500">{p.surveyed}/{p.total_voters} ({p.completion_pct}%)</span>
                </div>
                <Progress value={p.completion_pct} className="h-2" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Surveyor Activity */}
      <Card className="border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Users className="w-5 h-5 text-emerald-500" /> Surveyor Activity
          </CardTitle>
        </CardHeader>
        <CardContent>
          <table className="w-full data-table">
            <thead>
              <tr>
                <th>Surveyor</th>
                <th>Assigned</th>
                <th>Completed</th>
                <th>Progress</th>
              </tr>
            </thead>
            <tbody>
              {stats.surveyors.map(s => (
                <tr key={s.id}>
                  <td className="font-medium text-slate-900">{s.name}</td>
                  <td className="text-right">{s.total_assigned}</td>
                  <td className="text-right">{s.completed}</td>
                  <td>
                    <div className="flex items-center gap-2">
                      <Progress value={s.completion_pct} className="h-2 flex-1" />
                      <span className="text-xs font-medium text-slate-500 w-12 text-right">{s.completion_pct}%</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
